import Vue from "vue";
import "./registerServiceWorker";
import router from "./router";
import store from "./store";
import VueTour from "vue-tour";
import VueTelInput from 'vue-tel-input';
import 'vue-tel-input/dist/vue-tel-input.css';

// SENTRY PRODUCTION CONFIGURATION
import * as Sentry from "@sentry/vue";
import { setupSentryAxiosInterceptor } from "./plugins/sentryAxiosInterceptor";

// REFRESH TOKEN INTERCEPTOR
import { setupRefreshTokenInterceptor } from "./plugins/refreshTokenInterceptor";

const isProduction = process.env.NODE_ENV === 'production';

if (isProduction) {
  Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    environment: 'production',
    
    // PRODUCTION OPTIMIZED RATES
    replaysSessionSampleRate: 0.1,    // 10% session sampling
    replaysOnErrorSampleRate: 1.0,    // 100% error replay capture
    
    integrations: [
      Sentry.browserTracingIntegration({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
      
      // SESSION REPLAY - DEBUGGING OPTIMIZED CONFIG
      Sentry.replayIntegration({
        
        // === DEBUGGING FIRST (Smart Privacy) ===
        maskAllText: false,          // Metinleri göster (debugging için gerekli)
        maskAllInputs: false,       // Input'ları göster (form debug için gerekli)
        blockAllMedia: true,        // Sadece media'yı blokla (bandwidth)
        
        // CRITICAL DATA PROTECTION
        mask: [
          'input[type="password"]',
          'input[type="email"]',
          'input[name*="card"]',
          'input[name*="cvv"]',
          'input[name*="ssn"]',
          '.password-field',
          '.credit-card',
          '.payment-info',
          '.private-info',
          '[data-private]'
        ],
        
        // COMPLETE BLOCKING FOR SENSITIVE AREAS
        block: [
          '.admin-panel',
          '.secret-content',
          '[data-secret]',
          '.sensitive-data'
        ],
        
        // NETWORK MONITORING - PRODUCTION FOCUSED
        networkDetailAllowUrls: [
          window.location.origin,
        ],
        networkCaptureBodies: false,  // Disable for privacy in production
        networkRequestHeaders: ['Content-Type'],
        networkResponseHeaders: ['Content-Type'],
        
        // PERFORMANCE OPTIMIZATIONS
        useCompression: true,
        
        // PRODUCTION EVENT SAMPLING
        beforeAddRecordingEvent: (event) => {
          if (event.type === 'mousemove') {
            return Math.random() > 0.9 ? event : null; // 10% sampling for mouse movements
          }
          return event;
        },
      }),
    ],
    
    // PERFORMANCE MONITORING
    tracesSampleRate: 0.1,
    
    // ERROR FILTERING FOR PRODUCTION
    beforeSend: (event, hint) => {
      if (event.exception) {
        const error = event.exception.values[0];
        if (error && error.value) {
          const ignoredErrors = [
            'ResizeObserver loop limit exceeded',
            'Non-Error promise rejection captured',
            'Network Error',
            'Loading chunk',
            'ChunkLoadError',
            'Script error',
            'TypeError: Load failed'
          ];
          
          if (ignoredErrors.some(ignored => error.value.includes(ignored))) {
            return null;
          }
        }
      }
      return event;
    },
    
    // USER CONTEXT FOR PRODUCTION
    beforeSendTransaction: (transaction) => {
      if (store.state.user) {
        Sentry.setUser({
          id: store.state.user.uid || store.state.user.id,
          username: store.state.user.username || store.state.user.email,
        });
      }
      return transaction;
    },
    
    // RELEASE TRACKING
    release: process.env.APP_VERSION || '0.2.0',
    
    // PRODUCTION ENVIRONMENT
    debug: false,
  });
  
  setupSentryAxiosInterceptor();
}

// Setup refresh token interceptor (always active, not just in production)
setupRefreshTokenInterceptor();

import "vue-multiselect/dist/vue-multiselect.min.css";

import App from "./App.vue";
import VModal from "vue-js-modal";
Vue.use(VModal, {
  componentName: "modal",
  dynamicDefault: { draggable: true, resizable: false },
});

import "../public/vue-multiselect-light.min.css";

import VueI18n from "vue-i18n";
import TURKISH_TRANSLATIONS from "./localization/tr";

Vue.use(VueI18n);
import "./assets/main.css";
import "vue-tour/dist/vue-tour.css";

import moment from "moment";
moment.locale("tr");

import VueSweetalert2 from "vue-sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";

Vue.use(Toast, {
  transition: "Vue-Toastification__bounce",
  maxToasts: 20,
  newestOnTop: true,
});

import commonMixins from "./plugins/commonMixins.js";

import * as VueGoogleMaps from "vue2-google-maps";
import { yesNoOptions } from "@/utils/detailData";
import GmapCluster from "vue2-google-maps/dist/components/cluster";
Vue.component("GmapCluster", GmapCluster);

Vue.use(VueGoogleMaps, {
  load: {
    libraries: "places,drawing,visualization",
    key: "AIzaSyAZZ4alC4_NjYgaAOSmqshej4AO21GPL00",
    v: '3.26'
  },
  installComponents: true 
});

Vue.use(VueSweetalert2);
Vue.use(VueTour);
Vue.use(VueTelInput);

const globalMixin = {
  mixins: [commonMixins],
  computed: {
    getProfileImage() {
      return "https://cdn-icons-png.flaticon.com/512/149/149071.png";
    },
    isMobile() {
      return window.innerWidth < 1200;
    },
    getNowDate() {
      return moment().format("YYYY-MM-DD");
    },
    getYesNoList() {
      return yesNoOptions;
    },
  },
  methods: {
    // PRODUCTION ERROR REPORTING
    reportError(error, context = {}) {
      if (isProduction) {
        Sentry.withScope((scope) => {
          Object.keys(context).forEach(key => {
            scope.setTag(key, context[key]);
          });
          
          if (this.$store.state.user) {
            scope.setUser({
              id: this.$store.state.user.uid || this.$store.state.user.id,
              username: this.$store.state.user.username || this.$store.state.user.email,
            });
          }
          
          scope.setTag('route', this.$route.name || this.$route.path);
          Sentry.captureException(error);
        });
      }
    },
    
    trackEvent(category, action, label = null, value = null) {
      if (!this.$gtag || typeof this.$gtag.event !== 'function') {
        return;
      }
      
      try {
        this.$gtag.event(action, {
          event_category: category,
          event_label: label,
          value: value,
        });
      } catch (error) {
        console.error("Analytics Error:", error);
      }
    },
    
    dateFormatter(date, time, isOnly = true) {
      if (isOnly) return moment(date).format("DD-MM-YYYY");
      else return moment(date + "T" + time).format("DD-MM-YYYY HH:mm");
    },
    
    Money(value) {
      let val = 0;
      if (value != 0 && value != undefined) {
        if (value.toString().includes(".")) {
          val = Number(value.toString().replace(",", ".")).toFixed(2);
        } else {
          val = Number(value).toFixed(2);
        }
      }
      const returnVal = val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      return returnVal;
    },
    
    async swalBox(
      icon,
      title,
      text,
      isConfirm = false,
      confirmText = "Tamam",
      rejectText = "İptal",
      isInput = false
    ) {
      if (isConfirm) {
        if (isInput) {
          return this.$swal.fire({
            icon: icon,
            title: title,
            text: text,
            input: "text",
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: confirmText,
            cancelButtonText: rejectText,
          });
        } else {
          return this.$swal.fire({
            icon: icon,
            title: title,
            text: text,
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: confirmText,
            cancelButtonText: rejectText,
          });
        }
      } else {
        return this.$swal.fire({
          icon: icon,
          title: title,
          text: text,
          showConfirmButton: true,
          showCancelButton: false,
          confirmButtonText: confirmText,
        });
      }
    },
    
    errorBox(result, title = "HATA !") {
      if (isProduction) {
        this.reportError(new Error(`API Error: ${result.data?.message || 'Unknown error'}`), {
          type: 'api_error',
          status: result.status,
          url: result.config?.url || 'unknown',
          method: result.config?.method || 'unknown'
        });
      }
      
      if (result.status == 403 || result.status == 401) {
        this.$swal
          .fire({
            icon: "error",
            title: title,
            text: "Oturum süreniz doldu giriş sayfasına yönlendiriliyorsunuz !",
          })
          .then((acceptState) => {
            if (acceptState.isConfirmed) {
              this.trackEvent("Error", "SessionTimeout");
              this.$store.commit("signOut");
              this.$router.push("/");
            }
          });
      } else {
        const errorMessage = result?.data?.message || result?.message || 'Bilinmeyen bir hata oluştu';
        this.$swal.fire({
          icon: "error",
          title: title,
          text: errorMessage,
        });
        this.trackEvent("Error", "ApiError", errorMessage);
      }
    },
    
    positionMarker(speed = 0, speedDirection = 0) {
      let icon = null;
      if (speed == 0 || speedDirection == 6) {
        icon = require("@/assets/marker/2.png");
      } else {
        switch (speedDirection) {
          case 0:
            icon = require("@/assets/marker/0.png");
            break;
          case 1:
            icon = require("@/assets/marker/1.png");
            break;
          case 2:
            icon = require("@/assets/marker/8.png");
            break;
          case 3:
            icon = require("@/assets/marker/3.png");
            break;
          case 4:
            icon = require("@/assets/marker/4.png");
            break;
          case 5:
            icon = require("@/assets/marker/7.png");
            break;
          default:
            icon = require("@/assets/marker/5.png");
            break;
        }
      }
      return icon;
    },
    
    signOut() {
      this.swalBox(
        "warning",
        "UYARI !",
        "Çıkış yapmak istediğinize emin misiniz",
        true,
        "Evet,eminim",
        "Hayır Değilim"
      ).then((acc) => {
        if (acc.isConfirmed) {
          clearInterval(this.$store.state.intervalId);
          clearInterval(this.$store.state.sosIntervalId);
          clearInterval(this.$store.state.LTTIntervalId);

          this.$store.commit("clearActiveTab");
          this.$store.commit("pages/clearPage");
          this.$store.commit("activeLightMode");
          document.documentElement.classList.remove("dark");
          this.$store.commit("signOut");
          this.$router.push("/");
        }
      });
    },
    
    getLastThreeWords(address) {
      if (address) {
        const words = address.split(" ");
        const lastThreeWords =
          words.length <= 3
            ? address
            : words[words.length - 2] + " / " + words[words.length - 3];
        return lastThreeWords;
      } else return;
    },
  },
};

Vue.mixin(globalMixin);

Vue.config.productionTip = false;
const TRANSLATIONS = {
  tr: TURKISH_TRANSLATIONS,
};

const i18n = new VueI18n({
  locale: "tr",
  messages: TRANSLATIONS,
});

new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App),
}).$mount("#app");